import { useState } from "react";
import { AlertData, DealData } from "@/pages/deals/types";
import { useUpdateAlertMutation } from "@/pages/deals/hooks";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import ToastBody from "@/components/ui/toast-body";
import { toastIcon } from "@/components/ui/toast-icons";

const TRANSLATION_KEY = "deals.alerts.popover.alert";

interface UseAlertActionsProps {
  alert: AlertData;
  dealId: string;
  onAlertUpdate?: (newDealAlerts: AlertData[]) => void;
  refetchKanbanColumn?: (updatedDeal: DealData) => void;
  isLastAlert?: boolean;
}

export const useAlertActions = ({
  alert,
  dealId,
  onAlertUpdate,
  refetchKanbanColumn,
  isLastAlert = false
}: UseAlertActionsProps) => {
  const { t } = useTranslation();
  const updateAlert = useUpdateAlertMutation();

  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldHide, setShouldHide] = useState(false);

  const handleUndo = async (newIsDismissedValue: boolean) => {
    const updatedDeal = await updateAlert.mutateAsync({
      pathParams: { dealId, alertId: alert.id },
      data: {
        isDismissed: newIsDismissedValue
      }
    });

    onAlertUpdate?.(updatedDeal?.alerts || []);
    refetchKanbanColumn?.(updatedDeal);
  };

  const performUpdate = (shouldCloseAlert: boolean) => {
    updateAlert.mutate(
      {
        pathParams: { dealId, alertId: alert.id },
        data: {
          isDismissed: shouldCloseAlert
        }
      },
      {
        onSuccess: (updatedDeal: DealData) => {
          refetchKanbanColumn?.(updatedDeal);
          onAlertUpdate?.(updatedDeal?.alerts || []);

          if (isLastAlert) {
            // Short delay to allow parent to render empty state
            setTimeout(() => {
              setIsAnimating(false);
              setShouldHide(false);
            }, 100);
          } else {
            setIsAnimating(false);
            setShouldHide(false);
          }

          toast.success(
            <ToastBody
              title={t(
                `${TRANSLATION_KEY}.toast.${shouldCloseAlert ? "closed" : "reopened"}`
              )}
              actions={
                <Button
                  variant="secondaryNoBg"
                  onClick={() => {
                    handleUndo(!shouldCloseAlert);
                    toast.dismiss();
                  }}
                >
                  {t("undo")}
                </Button>
              }
            />,
            {
              duration: 5000,
              icon: toastIcon.info
            }
          );
        }
      }
    );
  };

  const handleCheck = () => {
    const shouldCloseAlert = !alert.isDismissed;
    setIsAnimating(true);

    if (isLastAlert) {
      performUpdate(shouldCloseAlert);
    } else {
      setTimeout(() => {
        setShouldHide(true);
        performUpdate(shouldCloseAlert);
      }, 1000);
    }
  };

  return {
    isAnimating,
    shouldHide,
    handleCheck,
    handleUndo
  };
};
